import { useState } from 'react'
import type { Leave } from '@lms/shared-types'
import { LEAVE_TYPE_LABEL } from '../../lib/leave-type-meta'
import { fmtDateRange } from '../../lib/format'
import { CANCEL_REQUEST_REASON_MIN } from '../../lib/leave-cancellation'
import { ConfirmationDialog } from '../primitives/ConfirmationDialog'

export interface CancelLeaveDialogProps {
  open: boolean
  leave: Leave
  /** Receives the cancellation reason — present only for approved leaves. */
  onConfirm: (reason?: string) => void
  onClose: () => void
  isPending: boolean
  error: string | null
}

/** "Cancel this leave?" / "Request cancellation?" confirmation dialog.
 *
 * Pending / escalated leaves are withdrawn with one click. An already-approved
 * leave can't be voided directly — confirming files a cancellation request
 * (with a required reason) that the requester's manager approves or rejects. */
export function CancelLeaveDialog({
  open,
  leave,
  onConfirm,
  onClose,
  isPending,
  error,
}: CancelLeaveDialogProps): JSX.Element | null {
  const isApproved = leave.status === 'approved'
  const [reason, setReason] = useState('')
  const reasonOk = reason.trim().length >= CANCEL_REQUEST_REASON_MIN

  const body = (
    <>
      <p style={{ margin: 0 }}>
        {isApproved ? (
          <>
            This leave is already approved. To cancel it you&apos;ll send a
            request to your approver for{' '}
            <strong>{LEAVE_TYPE_LABEL[leave.type]}</strong> leave on{' '}
            <strong>{fmtDateRange(leave.startDate, leave.endDate)}</strong>. The
            days stay booked until they approve. We&apos;ll let you know either
            way.
          </>
        ) : (
          <>
            You are about to cancel your{' '}
            <strong>{LEAVE_TYPE_LABEL[leave.type]}</strong> leave for{' '}
            <strong>{fmtDateRange(leave.startDate, leave.endDate)}</strong>.
            Pending days will be returned to your balance. This cannot be undone.
          </>
        )}
      </p>

      {isApproved && (
        <label style={{ display: 'block', marginTop: 12 }}>
          <span
            style={{
              display: 'block',
              fontSize: 13,
              fontWeight: 600,
              marginBottom: 6,
              color: 'var(--text-1)',
            }}
          >
            Reason for cancelling
          </span>
          <textarea
            data-testid="cancel-leave-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="e.g. Plans changed, no longer travelling"
            autoFocus
            style={{
              width: '100%',
              resize: 'vertical',
              padding: '8px 10px',
              fontSize: 14,
              borderRadius: 8,
              border: '1px solid var(--surface-line)',
              fontFamily: 'inherit',
            }}
          />
        </label>
      )}

      {error != null && (
        <div
          role="alert"
          style={{
            marginTop: 12,
            padding: '10px 12px',
            background: 'var(--danger-bg)',
            color: 'var(--danger-fg)',
            border: '1px solid var(--danger-line)',
            borderRadius: 10,
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}
    </>
  )

  return (
    <ConfirmationDialog
      open={open}
      onClose={onClose}
      onConfirm={() => onConfirm(isApproved ? reason.trim() : undefined)}
      confirmDisabled={isApproved && !reasonOk}
      title={isApproved ? 'Request cancellation?' : 'Cancel this leave?'}
      confirmLabel={isApproved ? 'Send request' : 'Cancel leave'}
      cancelLabel={isApproved ? 'Never mind' : 'Keep leave'}
      variant="danger"
      loading={isPending}
      data-testid="cancel-leave-dialog"
    >
      {body}
    </ConfirmationDialog>
  )
}
